import React, { useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar/Sidebar';
import { BackgroundEffects, PageHeader } from '../../common';
import UserInfo from '../../common/UserInfo';
import styles from './PageLayout.module.css';

export const navigationItems = [
  {
    id: 'projects',
    label: 'Projects',
    icon: 'dashboard',
    description: 'Manage TARA projects'
  },
  {
    id: 'library',
    label: 'Library',
    icon: 'library',
    description: 'Reusable TARA entities'
  },
  {
    id: 'vulnerability-management',
    label: 'Vulnerability Management',
    icon: 'shield',
    description: 'Manage SBOM vulnerabilities'
  },
  {
    id: 'document-assessment',
    label: 'Document Assessment',
    icon: 'checklist',
    description: 'Find ISO 21434 gaps in your existing TARA documents'
  }
];

/**
 * PageLayout - Shared layout component for all pages
 * Handles sidebar, page header, navigation, and main content layout
 *
 * @param {string} activeNavItem - ID of the active sidebar navigation item
 * @param {string} currentPage - Title displayed in the page header breadcrumb
 * @param {Array} breadcrumbItems - Array of {label, path} objects for breadcrumb navigation
 * @param {ReactNode} rightContent - Content for right side of header (defaults to UserInfo)
 * @param {ReactNode} statusBar - Optional status bar content for header
 * @param {string} contentClassName - Additional CSS class for content area
 * @param {boolean} useTopNavigation - If true, show navigation in header instead of sidebar
 * @param {ReactNode} children - Page-specific content
 */
const PageLayout = memo(({
  activeNavItem,
  currentPage,
  breadcrumbItems = [],
  rightContent,
  beforeBellContent,
  statusBar,
  children,
  contentClassName,
  disableScroll = false,
  useTopNavigation = true,
}) => {
  const navigate = useNavigate();
  const [sidebarExpanded, setSidebarExpanded] = useState(false);

  const handleNavigation = (item) => {
    // `results`, `integrations`, and `admin-configuration` entries removed
    // by DC-001 / DC-003 / FU-002A — those top-level routes were retired.
    const routes = {
      'projects': '/projects',
      'library': '/athena/library',
      'vulnerability-management': '/athena/vulnerability-management',
      'document-assessment': '/athena/document-assessment',
    };
    if (routes[item]) {
      navigate(routes[item]);
    }
  };

  return (
    <div className={styles.pageContainer}>
      <BackgroundEffects />

      {/* Conditionally render sidebar only when not using top navigation */}
      {!useTopNavigation && (
        <Sidebar
          expanded={sidebarExpanded}
          activeItem={activeNavItem}
          onNavigation={handleNavigation}
          onExpandedChange={setSidebarExpanded}
        />
      )}

      <main className={`${styles.mainContent} ${sidebarExpanded ? styles.expanded : ''} ${useTopNavigation ? styles.noSidebar : ''} ${disableScroll ? styles.noScroll : ''}`}>
        <PageHeader
          breadcrumbItems={breadcrumbItems}
          currentPage={currentPage}
          rightContent={rightContent || <UserInfo />}
          beforeBellContent={beforeBellContent}
          statusBar={statusBar}
          showNavigation={useTopNavigation}
          navigationItems={navigationItems}
          activeNavItem={activeNavItem}
          onNavigation={handleNavigation}
        />

        <div className={`${styles.contentArea} ${contentClassName || ''}`}>
          {children}
        </div>
      </main>
    </div>
  );
});

PageLayout.displayName = 'PageLayout';

export default PageLayout;
